/**
 * Browser sign-in plumbing (ADR-014).
 *
 * The API owns the whole OIDC flow: the browser only ever navigates to
 * `/auth/login` and posts to `/auth/logout`. No token, code or verifier is
 * visible to this code — the session cookie is HttpOnly, and the only value
 * read here is the CSRF token the shared HTTP layer echoes on mutations.
 */
import { beginSignOut, cancelSignOut, forgetIssuedCsrfToken, request } from '@/lib/api/http';
import { env } from '@/lib/env';
import { forgetStoredStepUps } from './stepUpStore';

/**
 * The page's own origin when the API is served from another one, else
 * `null`. A cross-origin web app must name its origin in `return_to`, and the
 * API returns there only if that origin is in its `APP_WEB_ORIGIN` list.
 */
function crossOriginPageOrigin(apiBaseUrl: string = env.apiBaseUrl): string | null {
  const page = globalThis.location?.origin;
  if (!page || page === 'null') return null;
  try {
    return new URL(apiBaseUrl).origin === page ? null : page;
  } catch {
    return null;
  }
}

/** Where the browser goes to sign in, coming back to `returnTo` afterwards. */
export function loginUrl(
  returnTo: string,
  reauthenticate = false,
  apiBaseUrl: string = env.apiBaseUrl,
): string {
  const pageOrigin = crossOriginPageOrigin(apiBaseUrl);
  const target = pageOrigin && returnTo.startsWith('/') ? `${pageOrigin}${returnTo}` : returnTo;
  const params = new URLSearchParams({ return_to: target });
  if (reauthenticate) params.set('reauth', '1');
  return `${apiBaseUrl}/auth/login?${params.toString()}`;
}

/** The page the person is on, as a same-origin path. */
export function currentPath(
  location: Pick<Location, 'pathname' | 'search'> = window.location,
): string {
  return `${location.pathname}${location.search}`;
}

/**
 * Ends the session on the server (the request carries the CSRF token).
 *
 * Resolves to the URL the browser must visit to end the identity provider's
 * session too (RP-initiated logout), or `null` when the provider offers none.
 * The local session is already revoked either way.
 *
 * From the moment the request is sent, a `401` from any other request is the
 * sign-out taking effect and no longer sends the browser to `/auth/login`
 * (which would race the sign-out navigation and could sign the person straight
 * back in). Only a failed sign-out lifts that again; a successful one ends
 * with the browser leaving the app.
 */
export async function signOut(): Promise<string | null> {
  beginSignOut();
  let answer: { end_session_url?: string } | undefined;
  try {
    answer = await request<{ end_session_url?: string } | undefined>('/auth/logout', {
      method: 'POST',
    });
  } catch (error) {
    cancelSignOut();
    throw error;
  }
  forgetIssuedCsrfToken();
  // Actions waiting for a fresh sign-in were this person's (ADR-068).
  forgetStoredStepUps();
  return typeof answer?.end_session_url === 'string' ? answer.end_session_url : null;
}

/** The route shown after signing out; it needs no session. */
export const SIGNED_OUT_PATH = '/signed-out';
